import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Plus, X, Edit2, Trash2, Building2, ToggleLeft, ToggleRight, Search } from 'lucide-react';
import api from '../../api/client';

const EMPTY_FORM = { name: '', loyaltyTenantKey: '', description: '', accessApps: false };

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h3 className="font-heading text-lg font-bold text-[#561D1A]">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function ConfirmDialog({ message, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl p-6 text-center">
        <Trash2 size={32} className="mx-auto mb-3 text-red-400" />
        <p className="text-gray-700 mb-5">{message}</p>
        <div className="flex gap-3 justify-center">
          <button onClick={onCancel} className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm">Cancel</button>
          <button onClick={onConfirm} className="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 text-sm">Delete</button>
        </div>
      </div>
    </div>
  );
}

export default function MallsTab() {
  const [malls, setMalls]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [modalOpen, setModalOpen]   = useState(false);
  const [editMall, setEditMall]     = useState(null);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [saving, setSaving]         = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toggling, setToggling]     = useState(null);

  const load = () => {
    setLoading(true);
    api.get('/api/config/admin/malls')
      .then(res => setMalls(res.data))
      .catch(() => toast.error('Failed to load malls'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditMall(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEdit = (mall) => {
    setEditMall(mall);
    setForm({
      name:             mall.name || '',
      loyaltyTenantKey: mall.loyaltyTenantKey || '',
      description:      mall.description || '',
      accessApps:       mall.accessApps || false,
    });
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editMall) {
        await api.put(`/api/config/admin/malls/${editMall.id}`, form);
        toast.success('Mall updated');
      } else {
        await api.post('/api/config/admin/malls', form);
        toast.success('Mall created');
      }
      setModalOpen(false);
      load();
    } catch (err) {
      toast.error(err.displayMessage || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAccessApps = async (mall) => {
    setToggling(mall.id);
    try {
      await api.patch(`/api/config/admin/malls/${mall.id}/access-apps`, {
        accessApps: !mall.accessApps,
      });
      toast.success(`Loyalty integration ${!mall.accessApps ? 'enabled' : 'disabled'} for ${mall.name}`);
      load();
    } catch (err) {
      toast.error(err.displayMessage || 'Toggle failed');
    } finally {
      setToggling(null);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/api/config/admin/malls/${deleteTarget.id}`);
      toast.success(`${deleteTarget.name} deleted`);
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err.displayMessage || 'Delete failed');
    }
  };

  const filtered = malls.filter(m =>
    m.name?.toLowerCase().includes(search.toLowerCase()) ||
    m.loyaltyTenantKey?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-bold text-[#561D1A]">Mall Configuration</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Each mall row drives STANDALONE vs INTEGRATED loyalty mode.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-[#EC1A23] text-white rounded-xl text-sm font-medium hover:bg-[#c5151d] transition-colors"
        >
          <Plus size={15} /> Add Mall
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-xs">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search malls…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#EC1A23]/30"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-gray-400 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <Building2 size={36} className="mx-auto mb-3 text-gray-300" />
            <p className="text-gray-400 text-sm">
              {malls.length === 0 ? 'No malls configured yet. Add the first one.' : 'No malls match your search.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                  <th className="px-5 py-3 text-left font-medium">Name</th>
                  <th className="px-5 py-3 text-left font-medium">Loyalty Tenant Key</th>
                  <th className="px-5 py-3 text-left font-medium hidden md:table-cell">Description</th>
                  <th className="px-5 py-3 text-center font-medium">Loyalty Integration</th>
                  <th className="px-5 py-3 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map(mall => (
                  <tr key={mall.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-3.5 font-medium text-gray-800">{mall.name}</td>
                    <td className="px-5 py-3.5">
                      <code className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                        {mall.loyaltyTenantKey}
                      </code>
                    </td>
                    <td className="px-5 py-3.5 text-gray-500 hidden md:table-cell max-w-xs truncate">
                      {mall.description || <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-5 py-3.5 text-center">
                      <button
                        onClick={() => handleToggleAccessApps(mall)}
                        disabled={toggling === mall.id}
                        className="inline-flex items-center gap-1.5 text-xs font-medium disabled:opacity-50 transition-colors"
                        title={mall.accessApps ? 'Click to disable' : 'Click to enable'}
                      >
                        {mall.accessApps ? (
                          <>
                            <ToggleRight size={20} className="text-emerald-500" />
                            <span className="text-emerald-600">Integrated</span>
                          </>
                        ) : (
                          <>
                            <ToggleLeft size={20} className="text-gray-400" />
                            <span className="text-gray-400">Standalone</span>
                          </>
                        )}
                      </button>
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEdit(mall)}
                          className="p-1.5 text-gray-400 hover:text-[#561D1A] hover:bg-gray-100 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(mall)}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info banner when no malls */}
      {!loading && malls.length === 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-700">
          <strong>No malls configured.</strong> The application is running in{' '}
          <span className="font-semibold">Standalone mode</span> — loyalty-app integration is disabled.
          Add a mall and enable <em>Loyalty Integration</em> to switch to Integrated mode.
        </div>
      )}

      {/* Create / Edit modal */}
      {modalOpen && (
        <Modal title={editMall ? 'Edit Mall' : 'Add Mall'} onClose={() => setModalOpen(false)}>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Mall Name <span className="text-red-400">*</span>
              </label>
              <input
                required
                maxLength={200}
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Grand Mall"
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EC1A23]/30"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Loyalty Tenant Key <span className="text-red-400">*</span>
              </label>
              <input
                required
                maxLength={100}
                value={form.loyaltyTenantKey}
                onChange={e => setForm(f => ({ ...f, loyaltyTenantKey: e.target.value }))}
                placeholder="e.g. grand-mall-001"
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#EC1A23]/30"
              />
              <p className="text-xs text-gray-400 mt-1">
                Must match <code>loyalty_db.tenants.external_key</code> exactly.
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
              <textarea
                maxLength={1000}
                rows={2}
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Optional description"
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EC1A23]/30 resize-none"
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
              <div>
                <p className="text-sm font-medium text-gray-700">Loyalty Integration</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  Enables bidirectional sync with the loyalty mobile app.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, accessApps: !f.accessApps }))}
                className="ml-4 flex-shrink-0"
              >
                {form.accessApps ? (
                  <ToggleRight size={28} className="text-emerald-500" />
                ) : (
                  <ToggleLeft size={28} className="text-gray-300" />
                )}
              </button>
            </div>

            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 px-4 py-2 bg-[#EC1A23] text-white rounded-xl text-sm font-medium hover:bg-[#c5151d] disabled:opacity-50 transition-colors"
              >
                {saving ? 'Saving…' : editMall ? 'Update Mall' : 'Create Mall'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <ConfirmDialog
          message={`Delete "${deleteTarget.name}"? This cannot be undone.`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
